import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Bug, Plus } from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { useCan, useIsPlatform } from '@/store/auth';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge, StatusBadge } from '@/components/ui/Badge';
import { Field, Input, Select, Textarea } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { EmptyState, ErrorState, TableSkeleton } from '@/components/ui/States';
import { Table, TableWrapper, Td, Th, Tr } from '@/components/ui/Table';
import { useToast } from '@/components/ui/Toast';
import { formatRelative } from '@/lib/format';
import { cn } from '@/lib/cn';

export const KATEGORI = {
  scraping: 'Scraping',
  deteksi: 'Deteksi Otomatis',
  penyimpanan: 'Penyimpanan',
  'data-publik': 'Data Publik',
  akun: 'Akun & Masuk',
  tampilan: 'Tampilan',
  performa: 'Performa',
  lainnya: 'Lainnya',
};

export const KEPARAHAN = {
  low: { label: 'Ringan', tone: 'neutral' },
  medium: { label: 'Sedang', tone: 'info' },
  high: { label: 'Berat', tone: 'warning' },
  critical: { label: 'Kritis', tone: 'danger' },
};

const SARINGAN_STATUS = [
  ['', 'Semua status'],
  ['open', 'Terbuka'],
  ['triaged', 'Ditriase'],
  ['in_progress', 'Dikerjakan'],
  ['resolved', 'Selesai'],
  ['wont_fix', 'Tidak Diperbaiki'],
  ['duplicate', 'Duplikat'],
];

/**
 * Batas panjang di sini dijaga sama dengan skema di server. Menolak lebih awal
 * di peramban jauh lebih ramah daripada membuang tulisan panjang pengguna
 * setelah tombol kirim ditekan.
 */
const schema = z.object({
  title: z.string().trim().min(5, 'Judul minimal 5 karakter.').max(140),
  category: z.string(),
  severity: z.string(),
  description: z
    .string()
    .trim()
    .min(20, 'Jelaskan minimal 20 karakter supaya bisa ditelusuri.')
    .max(5000),
  stepsToReproduce: z.string().trim().max(3000).optional(),
  expectedResult: z.string().trim().max(1000).optional(),
  actualResult: z.string().trim().max(1000).optional(),
});

const FormLaporan = ({ open, onClose }) => {
  const toast = useToast();
  const queryClient = useQueryClient();

  const form = useForm({
    resolver: zodResolver(schema),
    mode: 'onBlur',
    defaultValues: { category: 'lainnya', severity: 'medium' },
  });

  const mutation = useMutation({
    mutationFn: (values) =>
      api.post('/bug-reports', {
        ...values,
        // Halaman terakhir yang dibuka sangat membantu saat menelusuri;
        // sisa data lingkungan diambil server dari header permintaan.
        pageUrl: window.location.href,
      }),
    onSuccess: (hasil) => {
      queryClient.invalidateQueries({ queryKey: ['bug-reports'] });
      toast.success(`Laporan ${hasil?.data?.reference ?? ''} terkirim`, {
        description: 'Tim akan menindaklanjuti dan Anda dapat pemberitahuan.',
      });
      form.reset();
      onClose();
    },
    onError: (err) => {
      toast.error('Laporan gagal dikirim', {
        description: err instanceof ApiError ? err.message : 'Coba lagi sebentar.',
      });
    },
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Lapor Bug"
      description="Makin jelas langkahnya, makin cepat bisa kami perbaiki."
      size="lg"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            Batal
          </Button>
          <Button
            onClick={form.handleSubmit((v) => mutation.mutate(v))}
            loading={mutation.isPending}
          >
            Kirim Laporan
          </Button>
        </div>
      }
    >
      <form className="space-y-4" onSubmit={form.handleSubmit((v) => mutation.mutate(v))} noValidate>
        <Field label="Judul" required error={form.formState.errors.title?.message}>
          {(props) => (
            <Input
              {...props}
              {...form.register('title')}
              placeholder="Ringkas masalahnya dalam satu kalimat"
              error={form.formState.errors.title}
            />
          )}
        </Field>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Bagian" error={form.formState.errors.category?.message}>
            {(props) => (
              <Select {...props} {...form.register('category')}>
                {Object.entries(KATEGORI).map(([nilai, label]) => (
                  <option key={nilai} value={nilai}>
                    {label}
                  </option>
                ))}
              </Select>
            )}
          </Field>

          <Field label="Tingkat Keparahan" error={form.formState.errors.severity?.message}>
            {(props) => (
              <Select {...props} {...form.register('severity')}>
                {Object.entries(KEPARAHAN).map(([nilai, { label }]) => (
                  <option key={nilai} value={nilai}>
                    {label}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        </div>

        <Field
          label="Apa yang terjadi"
          required
          hint="Ceritakan masalahnya. Minimal 20 karakter."
          error={form.formState.errors.description?.message}
        >
          {(props) => (
            <Textarea
              {...props}
              {...form.register('description')}
              rows={4}
              placeholder="Saat menekan tombol Deteksi Otomatis, halaman berhenti di 'Menganalisa'…"
              error={form.formState.errors.description}
            />
          )}
        </Field>

        <Field
          label="Langkah untuk mengulangi"
          hint="Opsional, tapi ini yang paling menentukan cepat-tidaknya diperbaiki."
          error={form.formState.errors.stepsToReproduce?.message}
        >
          {(props) => (
            <Textarea
              {...props}
              {...form.register('stepsToReproduce')}
              rows={3}
              placeholder={'1. Buka Buat Scraping\n2. Tempel URL …\n3. Klik Deteksi Otomatis'}
            />
          )}
        </Field>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Yang diharapkan" error={form.formState.errors.expectedResult?.message}>
            {(props) => (
              <Textarea {...props} {...form.register('expectedResult')} rows={2} />
            )}
          </Field>
          <Field label="Yang terjadi" error={form.formState.errors.actualResult?.message}>
            {(props) => <Textarea {...props} {...form.register('actualResult')} rows={2} />}
          </Field>
        </div>

        <p className="text-xs text-[var(--text-muted)]">
          Peramban, versi aplikasi, dan halaman yang sedang dibuka ikut terkirim otomatis supaya
          Anda tidak perlu mengetiknya.
        </p>
      </form>
    </Modal>
  );
};

/**
 * Daftar laporan bug.
 *
 * Anggota biasa hanya melihat laporannya sendiri. Admin ruang kerja bisa
 * beralih ke seluruh laporan ruang kerjanya, dan staf platform (admin, dev,
 * root) ke seluruh laporan dari semua pengguna.
 *
 * Yang berbeda dari melihat adalah menangani: triase dan catatan internal
 * hanya untuk staf platform. Lihat alasannya di `bug-report.service.js`.
 *
 * Penyaringan itu ditegakkan di server. Sakelar di sini hanya mengubah
 * permintaan, bukan hak aksesnya.
 */
export const BugReportsPage = () => {
  const [buka, setBuka] = useState(false);
  const [status, setStatus] = useState('');
  const [scope, setScope] = useState('mine');
  // Dua jalur sah sekaligus: admin di ruang kerjanya sendiri, dan staf
  // platform yang menangani laporan dari seluruh ruang kerja. Yang kedua
  // itulah yang membuat menu ini berguna bagi yang membangun Crawel, bukan
  // cuma bagi admin di ruang kerja si pelapor.
  const stafPlatform = useIsPlatform('admin', 'dev');
  const bolehTriase = useCan('admin') || stafPlatform;

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['bug-reports', { status, scope }],
    queryFn: () => api.get('/bug-reports', { params: { status, scope, limit: 50 } }),
  });

  const laporan = data?.data ?? [];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-[var(--text-primary)]">Lapor Bug</h1>
          <p className="mt-1 text-sm text-[var(--text-secondary)]">
            Laporkan yang tidak beres, lalu ikuti perkembangannya di sini.
          </p>
        </div>
        <Button icon={Plus} onClick={() => setBuka(true)}>
          Buat Laporan
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {SARINGAN_STATUS.map(([nilai, label]) => (
          <button
            key={nilai || 'semua'}
            type="button"
            onClick={() => setStatus(nilai)}
            className={cn(
              'rounded-full px-3 py-1.5 text-xs font-semibold transition-colors',
              status === nilai
                ? 'bg-brand-600 text-white'
                : 'bg-[var(--surface-sunken)] text-[var(--text-secondary)] hover:text-[var(--text-primary)]',
            )}
          >
            {label}
          </button>
        ))}

        {bolehTriase && (
          <div className="ml-auto flex items-center gap-1 rounded-lg bg-[var(--surface-sunken)] p-1">
            {[
              ['mine', 'Laporan saya'],
              ['all', stafPlatform ? 'Semua pengguna' : 'Semua laporan'],
            ].map(([nilai, label]) => (
              <button
                key={nilai}
                type="button"
                onClick={() => setScope(nilai)}
                className={cn(
                  'rounded-md px-3 py-1.5 text-xs font-semibold transition-colors',
                  scope === nilai
                    ? 'bg-[var(--surface-raised)] text-[var(--text-primary)] shadow-xs'
                    : 'text-[var(--text-muted)] hover:text-[var(--text-secondary)]',
                )}
              >
                {label}
              </button>
            ))}
          </div>
        )}
      </div>

      <Card>
        {isLoading ? (
          <TableSkeleton rows={5} columns={5} />
        ) : isError ? (
          <ErrorState message={error?.message} requestId={error?.requestId} onRetry={refetch} />
        ) : laporan.length === 0 ? (
          <EmptyState
            icon={Bug}
            title="Belum ada laporan"
            description={
              status
                ? 'Tidak ada laporan dengan status tersebut.'
                : 'Menemukan yang tidak beres? Laporkan supaya bisa kami perbaiki.'
            }
            action={
              <Button icon={Plus} onClick={() => setBuka(true)}>
                Buat Laporan
              </Button>
            }
          />
        ) : (
          <TableWrapper>
            <Table>
              <thead>
                <Tr>
                  <Th>Tiket</Th>
                  <Th>Judul</Th>
                  <Th>Bagian</Th>
                  <Th>Keparahan</Th>
                  <Th>Status</Th>
                  <Th>Dilaporkan</Th>
                </Tr>
              </thead>
              <tbody>
                {laporan.map((r) => {
                  const parah = KEPARAHAN[r.severity] ?? KEPARAHAN.medium;
                  return (
                    <Tr key={r.id}>
                      <Td>
                        <Link
                          to={`/lapor-bug/${r.id}`}
                          className="font-mono text-xs font-bold text-brand-600 hover:underline dark:text-brand-400"
                        >
                          {r.reference}
                        </Link>
                      </Td>
                      <Td>
                        <Link
                          to={`/lapor-bug/${r.id}`}
                          className="font-medium text-[var(--text-primary)] hover:underline"
                        >
                          {r.title}
                        </Link>
                      </Td>
                      <Td className="text-[var(--text-secondary)]">
                        {KATEGORI[r.category] ?? r.category}
                      </Td>
                      <Td>
                        <Badge tone={parah.tone} size="sm">
                          {parah.label}
                        </Badge>
                      </Td>
                      <Td>
                        <StatusBadge status={r.status} size="sm" />
                      </Td>
                      <Td className="text-[var(--text-muted)]">{formatRelative(r.createdAt)}</Td>
                    </Tr>
                  );
                })}
              </tbody>
            </Table>
          </TableWrapper>
        )}
      </Card>

      <FormLaporan open={buka} onClose={() => setBuka(false)} />
    </div>
  );
};
